import React from 'react';
import Svg, {Circle, Path, Rect} from 'react-native-svg';

export type MuseumArtKey =
  | 'painting'
  | 'statue'
  | 'vase'
  | 'mask'
  | 'fossil'
  | 'globe'
  | 'telescope'
  | 'crown'
  | 'scroll'
  | 'palette'
  | 'dinosaur'
  | 'camera';

export type MuseumArtItem = {
  key: MuseumArtKey;
  label: string;
  colors: {a: string; b: string; c: string};
  Render: React.ComponentType<{size: number}>;
};

const IconFrame = ({
  size,
  bg,
  children,
}: {
  size: number;
  bg: string;
  children: React.ReactNode;
}) => {
  return (
    <Svg width={size} height={size} viewBox="0 0 64 64">
      <Rect x="3" y="3" width="58" height="58" rx="18" fill={bg} />
      {children}
    </Svg>
  );
};

const Painting = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#FFE66D">
    <Rect x="14" y="14" width="36" height="36" rx="10" fill="#FFFFFF" />
    <Rect x="18" y="18" width="28" height="28" rx="8" fill="#7BDFF2" />
    <Path d="M19 41 L28 31 L34 38 L39 34 L46 43 L19 43 Z" fill="#2EC4B6" />
    <Circle cx="38" cy="27" r="5" fill="#FF6B6B" />
  </IconFrame>
);

const Statue = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#BEE3F8">
    <Path
      d="M28 14c3 0 6 2 6 6v2c0 2-1 3-2 4l2 4h-8l2-4c-1-1-2-2-2-4v-2c0-4 2-6 2-6z"
      fill="#6C63FF"
    />
    <Path d="M24 34h16c0 8-4 14-8 14s-8-6-8-14z" fill="#FF8FAB" />
    <Rect x="18" y="48" width="28" height="6" rx="3" fill="#2EC4B6" />
  </IconFrame>
);

const Vase = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#FFD6A5">
    <Path
      d="M26 16h12c0 6-3 8-3 10 0 2 4 6 4 12 0 9-5 14-7 14s-7-5-7-14c0-6 4-10 4-12 0-2-3-4-3-10z"
      fill="#FF006E"
    />
    <Path d="M27 38c3-2 7-2 10 0" stroke="#FFFFFF" strokeWidth="3" />
    <Circle cx="25" cy="30" r="3" fill="#3A86FF" />
    <Circle cx="39" cy="30" r="3" fill="#3A86FF" />
  </IconFrame>
);

const Mask = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#C7F9CC">
    <Path
      d="M18 22c6-6 22-6 28 0v12c0 12-6 20-14 20s-14-8-14-20V22z"
      fill="#00BBF9"
    />
    <Circle cx="26" cy="34" r="4" fill="#FFFFFF" />
    <Circle cx="38" cy="34" r="4" fill="#FFFFFF" />
    <Path d="M24 44c5 4 11 4 16 0" stroke="#FF006E" strokeWidth="4" />
  </IconFrame>
);

const Fossil = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#E9D8A6">
    <Path
      d="M20 34c0-8 6-14 14-14s14 6 14 14-6 14-14 14-14-6-14-14z"
      fill="#9B5DE5"
    />
    <Path
      d="M26 32h12M24 38h16M28 26c2 2 6 2 8 0"
      stroke="#FFFFFF"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <Circle cx="28" cy="32" r="2" fill="#FFFFFF" />
    <Circle cx="36" cy="32" r="2" fill="#FFFFFF" />
  </IconFrame>
);

const Globe = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#BDE0FE">
    <Circle cx="32" cy="30" r="16" fill="#00B4D8" />
    <Path
      d="M20 30c4-6 10-10 16-10 5 0 7 2 12 5-2 7-8 13-16 13-4 0-8-2-12-8z"
      fill="#2EC4B6"
    />
    <Path d="M16 50h32" stroke="#FF006E" strokeWidth="4" />
    <Rect x="28" y="46" width="8" height="6" rx="3" fill="#FF006E" />
  </IconFrame>
);

const Telescope = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#E0FBFC">
    <Path d="M18 26l26-8 4 12-26 8-4-12z" fill="#3A86FF" />
    <Circle cx="20" cy="28" r="4" fill="#FF006E" />
    <Path d="M30 36l-10 16" stroke="#2EC4B6" strokeWidth="4" />
    <Path d="M36 38l6 14" stroke="#2EC4B6" strokeWidth="4" />
    <Path d="M26 44h20" stroke="#FF8FAB" strokeWidth="4" />
  </IconFrame>
);

const Crown = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#FFF3B0">
    <Path d="M18 40l6-16 8 10 8-10 6 16v6H18v-6z" fill="#F72585" />
    <Circle cx="24" cy="24" r="4" fill="#3A86FF" />
    <Circle cx="32" cy="22" r="4" fill="#4CC9F0" />
    <Circle cx="40" cy="24" r="4" fill="#3A86FF" />
    <Rect x="18" y="46" width="28" height="6" rx="3" fill="#2EC4B6" />
  </IconFrame>
);

const Scroll = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#FFE5EC">
    <Path
      d="M22 20h20c4 0 6 2 6 6v12c0 4-2 6-6 6H26c-4 0-6 2-6 6v2"
      stroke="#9B5DE5"
      strokeWidth="5"
      strokeLinecap="round"
    />
    <Path
      d="M22 20c-4 0-6 2-6 6v18c0 4 2 6 6 6"
      stroke="#FF006E"
      strokeWidth="5"
      strokeLinecap="round"
    />
    <Path d="M26 30h16M26 36h12" stroke="#2EC4B6" strokeWidth="4" />
  </IconFrame>
);

const Palette = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#D8F3DC">
    <Path
      d="M34 16c-10 0-18 8-18 18 0 10 8 18 18 18 6 0 6-6 2-6h-1c-2 0-4-2-4-4 0-4 6-4 10-4 5 0 9-4 9-9 0-7-6-13-16-13z"
      fill="#FFB703"
    />
    <Circle cx="24" cy="30" r="3" fill="#3A86FF" />
    <Circle cx="31" cy="26" r="3" fill="#FF006E" />
    <Circle cx="39" cy="27" r="3" fill="#2EC4B6" />
    <Circle cx="44" cy="33" r="3" fill="#9B5DE5" />
  </IconFrame>
);

const Dinosaur = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#CDEAC0">
    <Path
      d="M18 38c0-10 8-18 18-18h6c2 0 4 2 4 4v4h-6c-3 0-6 3-6 6v6H18v-2z"
      fill="#00BBF9"
    />
    <Circle cx="44" cy="24" r="3" fill="#FF006E" />
    <Path
      d="M24 44h22"
      stroke="#FFB703"
      strokeWidth="5"
      strokeLinecap="round"
    />
    <Path d="M28 34h4" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
  </IconFrame>
);

const Camera = ({size}: {size: number}) => (
  <IconFrame size={size} bg="#CAF0F8">
    <Rect x="16" y="24" width="32" height="22" rx="8" fill="#023E8A" />
    <Rect x="22" y="20" width="10" height="6" rx="3" fill="#FFB703" />
    <Circle cx="32" cy="35" r="7" fill="#00BBF9" />
    <Circle cx="32" cy="35" r="3" fill="#FFFFFF" />
    <Circle cx="44" cy="28" r="2" fill="#FF006E" />
  </IconFrame>
);

export const MUSEUM_ART: MuseumArtItem[] = [
  {
    key: 'painting',
    label: 'Painting',
    colors: {a: '#FFE66D', b: '#7BDFF2', c: '#FF6B6B'},
    Render: Painting,
  },
  {
    key: 'statue',
    label: 'Statue',
    colors: {a: '#BEE3F8', b: '#6C63FF', c: '#FF8FAB'},
    Render: Statue,
  },
  {
    key: 'vase',
    label: 'Vase',
    colors: {a: '#FFD6A5', b: '#FF006E', c: '#3A86FF'},
    Render: Vase,
  },
  {
    key: 'mask',
    label: 'Mask',
    colors: {a: '#C7F9CC', b: '#00BBF9', c: '#FF006E'},
    Render: Mask,
  },
  {
    key: 'fossil',
    label: 'Fossil',
    colors: {a: '#E9D8A6', b: '#9B5DE5', c: '#FFFFFF'},
    Render: Fossil,
  },
  {
    key: 'globe',
    label: 'Globe',
    colors: {a: '#BDE0FE', b: '#00B4D8', c: '#2EC4B6'},
    Render: Globe,
  },
  {
    key: 'telescope',
    label: 'Telescope',
    colors: {a: '#E0FBFC', b: '#3A86FF', c: '#2EC4B6'},
    Render: Telescope,
  },
  {
    key: 'crown',
    label: 'Crown',
    colors: {a: '#FFF3B0', b: '#F72585', c: '#3A86FF'},
    Render: Crown,
  },
  {
    key: 'scroll',
    label: 'Scroll',
    colors: {a: '#FFE5EC', b: '#9B5DE5', c: '#2EC4B6'},
    Render: Scroll,
  },
  {
    key: 'palette',
    label: 'Palette',
    colors: {a: '#D8F3DC', b: '#FFB703', c: '#FF006E'},
    Render: Palette,
  },
  {
    key: 'dinosaur',
    label: 'Dinosaur',
    colors: {a: '#CDEAC0', b: '#00BBF9', c: '#FFB703'},
    Render: Dinosaur,
  },
  {
    key: 'camera',
    label: 'Camera',
    colors: {a: '#CAF0F8', b: '#023E8A', c: '#00BBF9'},
    Render: Camera,
  },
];
